import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { authService } from '../services/auth.service';
import { useBoundStore } from '@/stores';
import { getAccessToken, decodeJwt } from '@/lib/api-client';
import type {
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  User,
} from '../types/auth.types';
import {
  isMfaEnrollmentRequired,
  isMfaRequired,
  isVerificationRequired,
} from '../types/auth.types';
import { mapLoginResponseToAuthSession } from '../utils/auth-session.mapper';

// Query keys
export const authKeys = {
  all: ['auth'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
  sessions: () => [...authKeys.all, 'sessions'] as const,
};

/**
 * Hook to get current user profile
 * Only fetches if user has a token
 */
export function useProfile() {
  const hasToken = !!getAccessToken();

  return useQuery({
    queryKey: authKeys.profile(),
    queryFn: () => authService.getProfile(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });
}

/**
 * Hook for login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (credentials: LoginRequest) => authService.login(credentials),
    onSuccess: (data) => {
      // If verification is required, don't proceed with login
      if (isVerificationRequired(data)) {
        toast.info(data.message || 'Please verify your account before logging in.');
        return;
      }

      if (isMfaRequired(data)) {
        toast.info(data.message || 'Enter your MFA code to finish signing in.');
        return;
      }

      if (isMfaEnrollmentRequired(data)) {
        toast.info(data.message || 'Set up MFA to finish signing in.');
        return;
      }

      // At this point, data is narrowed to LoginResponse
      const loginData = data;

      // Decode JWT for non-authorizing org context only.
      const jwtPayload = decodeJwt(loginData.accessToken);
      const { user: enrichedUser, tenant } = mapLoginResponseToAuthSession(loginData, jwtPayload);

      // Update Zustand store
      const { login: storeLogin } = useBoundStore.getState();
      storeLogin(enrichedUser, tenant);
      // Invalidate and refetch profile
      void queryClient.invalidateQueries({ queryKey: authKeys.profile() });
      toast.success('Welcome back!');
      try {
        navigate({ to: '/dashboard' });
      } catch {
        // Ignore navigation errors in tests
      }
    },
    onError: (error: Error & { response?: { status?: number; data?: { message?: string } } }) => {
      const status = error.response?.status;
      const backendMessage = error.response?.data?.message;

      if (status === 401) {
        if (backendMessage?.toLowerCase().includes('locked')) {
          toast.error('Account Locked', {
            description: backendMessage,
          });
        } else if (backendMessage?.toLowerCase().includes('password sign-in is disabled')) {
          toast.error('Password Sign-In Disabled', {
            description: backendMessage,
          });
        } else if (backendMessage?.toLowerCase().includes('password has expired')) {
          toast.error('Password Expired', {
            description: backendMessage,
          });
        } else if (backendMessage?.toLowerCase().includes('too many')) {
          toast.error('Too Many Attempts', {
            description:
              'Your account has been temporarily locked due to multiple failed login attempts. Please try again later.',
          });
        } else {
          toast.error('Invalid Credentials', {
            description: 'The email or password you entered is incorrect. Please try again.',
          });
        }
      } else if (status === 502 || status === 503) {
        toast.error('Service Unavailable', {
          description:
            'The authentication service is temporarily unavailable. Please try again in a moment.',
        });
      } else if (status === 429) {
        toast.error('Too Many Requests', {
          description: 'Please wait a moment before trying again.',
        });
      } else {
        toast.error('Login Failed', {
          description: backendMessage || 'An unexpected error occurred. Please try again.',
        });
      }
    },
  });
}

/**
 * Hook for registration mutation
 * Note: Registration requires email verification before login
 */
export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: RegisterRequest) => authService.register(data),
    onSuccess: () => {
      // Registration successful - user needs to verify email before logging in
      // Don't try to log them in automatically
      toast.success('Account created! Please check your email to verify your account.');
      // Navigate to login page with a message
      try {
        navigate({ to: '/login' });
      } catch {
        // Ignore navigation errors in tests
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Registration failed. Please try again.';
      toast.error(message);
    },
  });
}

/**
 * Hook for logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear Zustand store
      const { logout: storeLogout } = useBoundStore.getState();
      storeLogout();
      // Clear all queries
      queryClient.clear();
      toast.success('Logged out successfully');
      try {
        navigate({ to: '/login' });
      } catch {
        // Ignore navigation errors in tests
      }
    },
    onError: () => {
      // Even on error, clear local state
      const { logout: storeLogout } = useBoundStore.getState();
      storeLogout();
      queryClient.clear();
      try {
        navigate({ to: '/login' });
      } catch {
        // Ignore navigation errors in tests
      }
    },
  });
}

/**
 * Hook for forgot password mutation
 */
export function useForgotPassword() {
  return useMutation({
    mutationFn: (data: ForgotPasswordRequest) => authService.forgotPassword(data),
    onSuccess: () => {
      toast.success('Password reset link sent! Check your email.');
    },
    onError: () => {
      // Always show success to prevent email enumeration
      toast.success('If an account exists with that email, you will receive a reset link.');
    },
  });
}

/**
 * Hook for reset password mutation
 */
export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: ResetPasswordRequest) => authService.resetPassword(data),
    onSuccess: () => {
      toast.success('Password reset successfully! Please log in.');
      try {
        navigate({ to: '/login' });
      } catch {
        // Ignore navigation errors in tests
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to reset password. The link may have expired.';
      toast.error(message);
    },
  });
}

/**
 * Hook for change password mutation (authenticated users)
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => authService.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully!');
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to change password.';
      toast.error(message);
    },
  });
}

/**
 * Hook for updating user profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Partial<User>) => authService.updateProfile(updates),
    onSuccess: (data) => {
      // Update Zustand store
      const { updateUser } = useBoundStore.getState();
      updateUser(data);
      // Update query cache
      queryClient.setQueryData(authKeys.profile(), data);
      toast.success('Profile updated successfully!');
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update profile.';
      toast.error(message);
    },
  });
}

/**
 * Hook for email verification
 */
export function useVerifyEmail() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (token: string) => authService.verifyEmail(token),
    onSuccess: () => {
      toast.success('Email verified successfully!');
      try {
        navigate({ to: '/dashboard' });
      } catch {
        // Ignore navigation errors in tests
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to verify email. The link may have expired.';
      toast.error(message);
    },
  });
}

/**
 * Hook for resending verification email
 */
export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => authService.resendVerification(email),
    onSuccess: () => {
      toast.success('Verification email sent!');
    },
    onError: () => {
      toast.error('Failed to send verification email. Please try again.');
    },
  });
}

/**
 * Combined hook for common auth state and actions
 */
export function useAuth() {
  const user = useBoundStore((state) => state.user);
  const tenant = useBoundStore((state) => state.tenant);
  const isAuthenticated = useBoundStore((state) => state.isAuthenticated);
  const hasPermission = useBoundStore((state) => state.hasPermission);
  const hasRole = useBoundStore((state) => state.hasRole);

  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const profileQuery = useProfile();

  return {
    // State
    user,
    tenant,
    isAuthenticated,
    isLoading: loginMutation.isPending || profileQuery.isLoading,

    // Permission checks
    hasPermission,
    hasRole,

    // Actions
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    logout: logoutMutation.mutate,
    logoutAsync: logoutMutation.mutateAsync,

    // Mutation states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
  };
}
